"use client";

import { BookOpen, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { HireBuilderTabPlaceholder } from "~/components/marketplace/hire-builder-tab-placeholder";
import { cn } from "~/lib/utils";

type SiteGlueExtendedKnowledgePanelProps = {
  businessName?: string;
  profileText: string;
  isGenerating?: boolean;
  className?: string;
  /** When true, profile grows with page scroll (no nested 42vh clip). */
  documentScroll?: boolean;
};

const PROSE =
  "prose prose-sm max-w-none text-slate-800 prose-headings:mb-2 prose-headings:mt-4 prose-headings:font-semibold prose-headings:text-slate-900 prose-hr:hidden prose-li:my-0.5 prose-p:my-2 prose-p:leading-relaxed prose-strong:font-semibold [&>*:first-child]:mt-0";

const SCAN_STEPS = [
  "Loading homepage…",
  "Scanning contact & location pages…",
  "Extracting services & team…",
  "Drafting Organization Profile…",
] as const;

/** Typical scrape + profile generation window for the progress UI. */
const EXPECTED_MS = 45_000;

/** Strip LLM doc titles that duplicate the panel chrome. */
export function sanitizeCompanyProfileMarkdown(text: string): string {
  return text
    .replace(/^\s*#+\s*Extended Knowledge Document\s*\n+/i, "")
    .replace(/^\s*Extended Knowledge Document\s*\n+/i, "")
    .trim();
}

export function BuildingCompanyProfileProgress({ className }: { className?: string }) {
  const [progress, setProgress] = useState(4);
  const [stepIndex, setStepIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(EXPECTED_MS / 1000));

  useEffect(() => {
    const started = Date.now();
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - started;
      const ratio = Math.min(elapsed / EXPECTED_MS, 1);
      // Ease toward ~92% while waiting; finish when parent clears isGenerating.
      setProgress(Math.min(4 + ratio * 88, 92));
      setSecondsLeft(Math.max(0, Math.ceil((EXPECTED_MS - elapsed) / 1000)));
      setStepIndex(Math.min(Math.floor(elapsed / 11_000), SCAN_STEPS.length - 1));
    }, 250);
    return () => window.clearInterval(tick);
  }, []);

  return (
    <div
      className={cn(
        "flex h-full min-h-[520px] flex-col items-center justify-center gap-4 bg-white p-8 text-center",
        className,
      )}
    >
      <Loader2 className="h-8 w-8 animate-spin text-[#4f81bd]" aria-hidden />
      <div>
        <p className="text-base font-semibold text-slate-900">Building Organization Profile…</p>
        <p className="mt-1 text-sm font-medium text-[#4f81bd]">{SCAN_STEPS[stepIndex]}</p>
        <p className="mt-2 max-w-sm text-sm text-slate-500">
          Trish is scraping your website and drafting what she learned about your organization.
          Your quick snapshot comes next.
        </p>
      </div>
      <div className="w-full max-w-sm space-y-2">
        <div
          className="h-2.5 overflow-hidden rounded-full bg-slate-200"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          aria-label="Organization Profile build progress"
        >
          <div
            className="h-full rounded-full bg-[#4f81bd] transition-[width] duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-xs text-slate-500">
          {secondsLeft > 0
            ? `About ${secondsLeft}s remaining…`
            : "Finishing up — almost ready…"}
        </p>
      </div>
    </div>
  );
}

export function SiteGlueExtendedKnowledgePanel({
  businessName,
  profileText,
  isGenerating = false,
  className,
  documentScroll = false,
}: SiteGlueExtendedKnowledgePanelProps) {
  const displayText = sanitizeCompanyProfileMarkdown(profileText);

  if (isGenerating && !displayText.trim()) {
    return <BuildingCompanyProfileProgress className={className} />;
  }

  if (!displayText.trim()) {
    return (
      <HireBuilderTabPlaceholder
        icon={BookOpen}
        title="What Trish Learned About Your Organization"
        description="Your services, locations, departments, team members, audiences, and organization goals will appear here."
        className={className}
      />
    );
  }

  return (
    <div
      className={cn(
        documentScroll
          ? "flex flex-col"
          : "flex h-full min-h-0 flex-col overflow-hidden",
        className,
      )}
    >
      <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 sm:px-5">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#4f81bd]">
          Organization Profile
        </p>
        <h2 className="mt-1 text-lg font-semibold text-slate-900">
          {businessName?.trim() || "Your organization"}
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Drafted from your website and updated as you confirm details with Trish.
        </p>
        {isGenerating ? (
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Refreshing profile…
          </p>
        ) : null}
      </div>
      <div
        className={cn(
          "px-4 py-4 sm:px-5",
          documentScroll ? "" : "min-h-0 flex-1 overflow-y-auto",
        )}
      >
        <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 sm:p-5">
          <ReactMarkdown remarkPlugins={[remarkGfm]} className={PROSE}>
            {displayText}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
